import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
  type ButtonProps,
  type PopoverProps,
  type SvgIconTypeMap,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import clsx from "clsx";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { ResponsiveButton } from "../../data-display/wrapper/ResponsiveButton";
import { MenuPopoverButton, MenuPopoverContainer, buttonClasses, popoverClasses } from "./styles";

export interface IMenuPopoverProps {
  className?: string;
  label: React.ReactNode;
  open?: boolean;
  anchorEl?: HTMLElement | null;
  hideTrigger?: boolean;
  openIconPosition?: "start" | "end";
  openIcons?: {
    open: OverridableComponent<SvgIconTypeMap<unknown, "svg">> & {
      muiName: string;
    };
    close: OverridableComponent<SvgIconTypeMap<unknown, "svg">> & {
      muiName: string;
    };
  };
  noIcons?: boolean;
  variant?: "filter" | "action" | "contained" | "sort";
  onClick?: () => void;
  onClose?: () => void;
  onOpen?: () => void;
  disabled?: boolean;
  ButtonProps?: Omit<ButtonProps, "open" | "ref" | "onClick">;
  PopoverProps?: Omit<PopoverProps, "open" | "anchorEl" | "onClose">;
  children: React.ReactNode;
  responsiveMode?: boolean;
  responsiveBreakpoint?: "tablet" | "desktop";
  responsiveIcon?: React.ReactNode;
}

const MenuPopoverLabel = ({ label, variant }: Pick<IMenuPopoverProps, "label" | "variant">) => {
  if (variant === "filter" && typeof label === "string") {
    return <Typography variant="body1">{label}</Typography>;
  }
  return <>{label}</>;
};

export const MenuPopover = ({
  className,
  label,
  open,
  anchorEl: controlledAnchorEl,
  hideTrigger = false,
  openIconPosition = "end",
  openIcons = { open: ArrowDropUpIcon, close: ArrowDropDownIcon },
  noIcons = false,
  variant = "filter",
  onClick,
  onClose,
  onOpen,
  disabled,
  ButtonProps,
  PopoverProps,
  children,
  responsiveMode = false,
  responsiveBreakpoint = "tablet",
  responsiveIcon,
}: IMenuPopoverProps) => {
  const [internalAnchor, setInternalAnchor] = useState<HTMLElement | null>(null);
  const [isOpen, setOpen] = useState(false);
  const theme = useTheme();
  const isResponsiveBreakpointReached = useMediaQuery(theme.breakpoints.down(responsiveBreakpoint));

  const anchor = controlledAnchorEl ?? internalAnchor;
  const isControlled = typeof open === "boolean";
  const openValue = isControlled ? open : isOpen;

  const handleClose = useCallback(() => {
    if (!isControlled) {
      setOpen(false);
    }
    if (onClose) {
      onClose();
    }
  }, [isControlled, onClose]);

  const toggleOpen = useCallback(() => {
    if (onClick) {
      onClick();
    }
    if (isControlled) {
      if (!open && onOpen) {
        onOpen();
      }
      if (open && onClose) {
        onClose();
      }
      return;
    }
    setOpen((previousOpen) => {
      if (previousOpen) {
        if (onClose) {
          onClose();
        }
        return false;
      }
      if (onOpen) {
        onOpen();
      }
      return true;
    });
  }, [isControlled, onClick, onOpen, onClose, open]);

  const shouldUseResponsiveButton = !hideTrigger && responsiveMode && isResponsiveBreakpointReached;

  const openIconProps = useMemo(() => {
    if (shouldUseResponsiveButton) {
      return { startIcon: responsiveIcon };
    }
    if (noIcons) {
      return {};
    }

    const Icon = openValue ? openIcons.open : openIcons.close;

    if (openIconPosition === "start") {
      return { startIcon: <Icon /> };
    }
    return { endIcon: <Icon /> };
  }, [noIcons, openIcons, openValue, openIconPosition, shouldUseResponsiveButton, responsiveIcon]);

  return (
    <>
      {!hideTrigger &&
        (shouldUseResponsiveButton ? (
          <div ref={setInternalAnchor}>
            <ResponsiveButton
              className={clsx(className, buttonClasses[variant])}
              type="button"
              variant={variant === "contained" ? variant : "outlined"}
              onClick={toggleOpen}
              disabled={disabled}
              breakpoint={responsiveBreakpoint}
              {...openIconProps}
              {...ButtonProps}
            />
          </div>
        ) : (
          <MenuPopoverButton
            className={clsx(className, buttonClasses[variant])}
            open={openValue}
            type="button"
            variant={variant === "contained" ? variant : "outlined"}
            onClick={toggleOpen}
            ref={setInternalAnchor}
            disabled={disabled}
            {...openIconProps}
            {...ButtonProps}
          >
            <MenuPopoverLabel label={label} variant={variant} />
          </MenuPopoverButton>
        ))}

      <MenuPopoverContainer
        className={clsx(className, popoverClasses.root, popoverClasses[variant])}
        minWidth={hideTrigger ? undefined : anchor?.clientWidth}
        open={openValue}
        anchorEl={anchor}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        elevation={0}
        onClose={handleClose}
        {...PopoverProps}
        slotProps={{
          ...PopoverProps?.slotProps,
          paper: {
            square: true,
            variant: "outlined",
            ...PopoverProps?.PaperProps,
            ...PopoverProps?.slotProps?.paper,
          },
        }}
      >
        {children}
      </MenuPopoverContainer>
    </>
  );
};
